import { useFormik, FormikHelpers, FieldArray, FormikProvider } from "formik";
import ModalBase from "../../../components/modals/ModalBase";
import { TAMModal } from "../../materials/components/AddMaterialModal";
import TextInputComp from "../../../components/inputs/TextInputComp";
import CustomDropDown from "../../../components/dropDown/CustDropDown";
import Checkbox from "../../../components/checkBox/CheckBox";
import Button from "../../../components/buttons/Button";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import * as Yup from "yup";
import apiRequest from "../../../helpers/apiRequest";
import { TSub, TUnit } from "./ExamsConfigs";
import { Urls } from "../../../utils/urls";
import { useEffect, useState } from "react";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  units: Yup.array()
    // .of(Yup.string().required("Required"))
    .min(1, "At least one unit is required"),
});

type TProps = {
  examId: string;
  examName: string;
  selSubject?: TSub;
};

const AddSubjectModal = ({
  isOpen,
  onClose,
  examId,
  examName,
  selSubject,
}: TProps & TAMModal) => {
  const [units, setUnits] = useState<Omit<TUnit, "subject">[]>([
    { name: "", _id: "" },
  ]); // units fetched seperately for update as subjects not contain unit data here.
  const [loading, setLoading] = useState(false);
  const onSubmit = (values: any, actions: FormikHelpers<any>) => {
    if (selSubject) {
      updateHandler(values, actions);
    } else {
      addHandler(values, actions);
    }
  };
  const addHandler = async (values: any, actions: FormikHelpers<any>) => {
    const { data, error } = await apiRequest<{ subject: TSub }>({
      method: "POST",
      url: Urls.subjects,
      data: { ...values, examId },
    });
    if (error) {
      alert(error);
    } else {
      resetForm();
      onClose();
      alert("Success");
    }
  };
  const updateHandler = async (values: any, actions: FormikHelpers<any>) => {
    const { data, error } = await apiRequest<{ subject: TSub }>({
      method: "PATCH",
      url: `${Urls.subjects}/${selSubject?._id}`,
      data: { ...values, examId },
    });
    if (error) {
      alert(error);
    } else {
      resetForm();
      onClose();
      alert("Success");
    }
  };
  const formik = useFormik({
    initialValues: {
      name: selSubject?.name ?? "",
      visible: true,
      units: units,

      // ...material,
      // exam: material?.exam?._id ?? undefined,
      // subject: material?.subject?._id ?? undefined,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });
  const {
    values,
    handleSubmit,
    handleChange,
    setFieldValue,
    isSubmitting,
    errors,
    submitForm,
    resetForm,
  } = formik;
  const onCancel = () => {
    const confirm = window.confirm(
      "Are you sure want to cancel? Your data wont be saved."
    );
    if (confirm) {
      resetForm();
      onClose();
    }
  };
  const getAllUnits = async () => {
    const { data, error } = await apiRequest<TUnit[]>({
      method: "GET",
      url: `${Urls.subjects}/${selSubject?._id}/units`,
    });
    if (error) {
      alert(error);
    } else if (data) {
      console.log(data);
      setLoading(false);
      setUnits(data);
    }
  };
  useEffect(() => {
    if (selSubject) {
      getAllUnits();
    }
  }, [selSubject]);
  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      title={selSubject ? "Edit Subject" : "Add Subject to " + examName}
      primaryBtn={{
        title: isSubmitting ? "Saving..." : selSubject ? "Update" : "Add Subject",
        onClick: submitForm,
        disabled: isSubmitting,
      }}
      secondaryBtn={{ title: "Cancel", onClick: onCancel }}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <TextInputComp
          id="name"
          name="name"
          required
          value={values.name}
          onChange={handleChange}
          label="Subject Name"
          placeholder="e.g. Mathematics, Physics"
          error={errors.name}
        />

        <div>
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Units in this Subject</h4>
          
          {typeof errors.units == "string" && (
            <p className="mb-3 text-sm text-red-500 bg-red-50 p-2 rounded-lg border border-red-100 italic">
              At least one unit is required to continue.
            </p>
          )}

          <FormikProvider value={formik}>
            <FieldArray
              name="units"
              render={({ push, remove }) => (
                <div className="space-y-4">
                  {values.units?.map((unit: any, index: number) => (
                    <div key={index} className="group relative flex items-start gap-3 p-4 bg-gray-50/50 rounded-2xl border border-gray-100 hover:border-primary/20 hover:bg-white hover:shadow-sm transition-all duration-200">
                        <div className="mt-7 flex-1">
                            <TextInputComp
                                id={`unit-${index}`}
                                label={`Unit ${index + 1} Name`}
                                name={`units[${index}].name`}
                                required
                                value={values.units[index]?.name}
                                onChange={handleChange}
                                placeholder={`Enter unit ${index + 1} name`}
                            />
                        </div>
                        
                        <button
                            type="button"
                            onClick={() => remove(index)}
                            className="mt-12 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            title="Remove Unit"
                        >
                            <TrashIcon className="w-5 h-5" />
                        </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => push({ name: "", _id: "" })}
                    className="w-full mt-4 flex items-center justify-center gap-2 py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:text-primary hover:border-primary/50 hover:bg-primary/5 transition-all group"
                  >
                    <PlusIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <span className="font-semibold text-sm uppercase tracking-wide">Add Another Unit</span>
                  </button>
                </div>
              )}
            />
          </FormikProvider>
        </div>
      </form>
    </ModalBase>
  );
};

export default AddSubjectModal;
